import { memo } from 'react';
import { format, formatDistanceToNow } from 'date-fns';
import { ArrowRight, Gavel, Lock, MessageSquare, Scale } from 'lucide-react';
import { AppealDetail } from '../../types/appeals';
import {
  epochToDate, formatBanLength, getV2Outcome, isV2Appeal,
  normalizeJuryStageVotes, V2_OUTCOME_META,
} from '../../utils/appealsV2';
import { decodeHtmlEntities } from '../../utils/decodeHtmlEntities';
import { cn } from '../../utils/cn';

interface V2OutcomeBannerProps {
  appeal: AppealDetail;
  className?: string;
}

function fmtExpiry(ts: number | null | undefined): string {
  if (ts === null || ts === undefined) return 'Permanent';
  const d = epochToDate(ts);
  return d ? format(d, 'PPP p') : '—';
}

/**
 * Outcome block for decided Jury V2 appeals — the V2 counterpart of the
 * V1 verdict banner (which keys off `verdict` and never fires for V2).
 * Renders nothing for V1 appeals or while the V2 jury is still out.
 */
export const V2OutcomeBanner = memo(({ appeal, className }: V2OutcomeBannerProps) => {
  if (!isV2Appeal(appeal)) return null;

  const outcome = getV2Outcome(appeal.v2_stage1_result, appeal.v2_stage2_result);
  if (!outcome) return null;

  const meta = V2_OUTCOME_META[outcome];
  const stageVotes = normalizeJuryStageVotes(appeal.jury_stage_votes);

  const stage1Uphold = stageVotes.stage1.filter(v => v.vote === 'uphold').length;
  const stage1Vary = stageVotes.stage1.filter(v => v.vote === 'vary').length;
  const stage2Remove = stageVotes.stage2.filter(v => v.vote === 'remove').length;
  const stage2Reduce = stageVotes.stage2.filter(v => v.vote === 'reduce').length;
  const stage2Increase = stageVotes.stage2.filter(v => v.vote === 'increase').length;

  // Before/after only when the backend actually applied a new expiry
  // (reduce, or increase on an expiring ban).
  const hasExpiryChange = appeal.v2_new_ban_expires !== null && appeal.v2_new_ban_expires !== undefined;
  const newExpiryDate = epochToDate(appeal.v2_new_ban_expires);
  const newExpiryInPast = newExpiryDate !== null && newExpiryDate.getTime() < Date.now();

  const madeUnappealable = appeal.v2_ban_made_unappealable === 1;
  const increaseTranslated = madeUnappealable
    && appeal.v2_proposed_increase_seconds !== null
    && appeal.v2_proposed_increase_seconds !== undefined;

  return (
    <div className={cn(
      'alert flex-col items-stretch gap-3',
      meta.approved ? 'alert-success' : 'alert-danger',
      className,
    )} role="status">
      <div className="flex flex-wrap items-center gap-2">
        <Gavel className={cn('w-4 h-4', meta.approved ? 'text-green-400' : 'text-red-400')} />
        <span className="font-semibold text-white">
          Jury Outcome: <span className={meta.text}>{outcome === 'made_unappealable' ? 'Made Unappealable' : meta.label}</span>
        </span>
        <span className="badge badge-purple">
          V2 · Two-Stage
        </span>
      </div>

      <p className="text-sm text-glass-300 ml-6">{meta.description}</p>

      {hasExpiryChange && (
        <div className="ml-6 flex flex-wrap items-center gap-2 text-sm">
          <span className="text-glass-400">Ban expiry:</span>
          <span className="font-mono text-xs tabular-nums text-glass-300 bg-glass-100/10 px-2 py-0.5 rounded-md">
            {fmtExpiry(appeal.v2_ban_expires_snapshot)}
          </span>
          <ArrowRight className="w-3.5 h-3.5 text-glass-400" />
          <span className={cn('font-mono text-xs tabular-nums px-2 py-0.5 rounded-md bg-glass-100/10', meta.text)}>
            {fmtExpiry(appeal.v2_new_ban_expires)}
          </span>
          {outcome === 'reduced' && newExpiryInPast && (
            <span className="text-xs text-green-400">(already in the past — effectively unenforced)</span>
          )}
        </div>
      )}

      {madeUnappealable && (
        <div className="ml-6 alert alert-warning">
          <Lock />
          <div className="text-sm">
            <p className="text-amber-300 font-semibold">Ban made unappealable</p>
            <p className="text-xs text-glass-400 mt-0.5">
              {increaseTranslated
                ? `The jury voted to extend by ${formatBanLength(appeal.v2_proposed_increase_seconds)}, applied as unappealable instead (permanent ban). The expiry was left untouched.`
                : 'The appealable flag on this ban was switched off. The player cannot appeal this ban again.'}
            </p>
          </div>
        </div>
      )}

      {outcome === 'reduced' && appeal.v2_proposed_reduce_seconds !== null && appeal.v2_proposed_reduce_seconds !== undefined && (
        <p className="ml-6 text-xs text-glass-400">
          Jury's chosen ban length: <span className="text-glass-300 font-medium">{formatBanLength(appeal.v2_proposed_reduce_seconds)}</span> from the original ban date.
        </p>
      )}

      {outcome === 'increased' && appeal.v2_proposed_increase_seconds !== null && appeal.v2_proposed_increase_seconds !== undefined && (
        <p className="ml-6 text-xs text-glass-400">
          Jury's chosen extension: <span className="text-glass-300 font-medium">+{formatBanLength(appeal.v2_proposed_increase_seconds)}</span> (plus the time spent under appeal).
        </p>
      )}

      {(stageVotes.stage1.length > 0 || stageVotes.stage2.length > 0) && (
        <div className="ml-6 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-glass-400 tabular-nums">
          <Scale className="w-3.5 h-3.5 text-glass-400" />
          {stageVotes.stage1.length > 0 && (
            <span>
              Stage 1: <span className="text-red-400 font-medium">{stage1Uphold} uphold</span>
              {' / '}<span className="text-blue-400 font-medium">{stage1Vary} vary</span>
            </span>
          )}
          {stageVotes.stage2.length > 0 && (
            <span>
              Stage 2: <span className="text-green-400 font-medium">{stage2Remove} remove</span>
              {' / '}<span className="text-teal-400 font-medium">{stage2Reduce} reduce</span>
              {' / '}<span className="text-orange-400 font-medium">{stage2Increase} increase</span>
            </span>
          )}
        </div>
      )}

      {appeal.jury_summary && (
        <div className="ml-6 flex items-start gap-2">
          <MessageSquare className="w-3.5 h-3.5 text-glass-400 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-glass-300 whitespace-pre-wrap">{decodeHtmlEntities(appeal.jury_summary)}</p>
        </div>
      )}

      {appeal.decision_reason && (
        <p className="text-sm text-glass-300 ml-6 whitespace-pre-wrap">{decodeHtmlEntities(appeal.decision_reason)}</p>
      )}

      {appeal.decided_at && (
        <p className="text-xs text-glass-400 ml-6 tabular-nums">
          Decided {(() => { const d = epochToDate(appeal.decided_at); return d ? formatDistanceToNow(d, { addSuffix: true }) : '—'; })()}
        </p>
      )}
    </div>
  );
});
